import {DocumentNode, gql} from '@apollo/client';

import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TypedValue} from 'notebook/tables/TableConstants';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import {
  ExperimentalInputs,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  prepareInitialExperimentalInputs,
} from 'studies/typed/shared/ExperimentalInputs';
import {EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT} from 'studies/typed/shared/ExperimentalInputs';

import {GetStudyDesignFromProcedureRunPlanGraphQL as ProcedureRunPlanNode} from './__generated/GetStudyDesignFromProcedureRunPlanGraphQL';

export interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
}

/**
 * Extracts the study design from a ProcedureRunPlanNode.
 *
 * @param procedureRunPlan - a ProcedureRunPlanNode that contains STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT
 * @returns an object of {experimentalInputs, templateFlowchart} for use with design components.
 */
export default function getStudyDesignFromProcedureRunPlan(
  procedureRunPlan: ProcedureRunPlanNode
): StudyDesignParams {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(procedureRunPlan);
  assert(flowchartVersion != null, 'Expected flowchart version to be present');
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');

  const nodes = flowchartVersion.nodes;
  const templateMethods: Array<ExperimentMethod> = sortNodesByCoordinates(
    [...nodes],
    'gridLayoutCoordinates'
  );

  const templateFlowchart: ExperimentFlowchart = {
    ...flowchartVersion,
    nodes: templateMethods,
  };

  const sortedConditions = [...procedureRunPlan.procedureRunPlanConditions].sort(
    (a, b) => a.position - b.position
  );

  // When there are no stored conditions, default to 1 condition to match the component's fallback.
  // This ensures experimentalInputs.values has the same length as initialConditions.
  const numConditions = sortedConditions.length > 0 ? sortedConditions.length : 1;

  const confirmationMethodsByParamVersionId = new Map(
    procedureRunPlan.confirmationMethodByParamVersionId.map((cm) => [
      cm.paramVersionId,
      cm.confirmationMethod,
    ])
  );

  const prevConfirmationMethods = Object.fromEntries(
    templateMethods.map((methodNode) => [
      methodNode.id,
      {
        parameterConfirmationMethods: getConfirmationMethodsForMethod(
          methodNode,
          confirmationMethodsByParamVersionId
        ),
      },
    ])
  );

  // Build planValues from procedureRunPlanConditions.methodParametersPlans
  const planValues = Object.fromEntries(
    templateMethods.map((methodNode) => [
      methodNode.id,
      {
        values: sortedConditions.map((condition) => {
          const methodPlan = condition.methodParametersPlans.find(
            (mp) => mp.templateMethodVersion.id === methodNode.id
          );

          return getValuesFromMethodPlan(methodNode, methodPlan);
        }),
      },
    ])
  );

  const experimentalInputs = prepareInitialExperimentalInputs(numConditions, templateMethods, {
    prevExperimentalInputValues: planValues,
    prevConfirmationMethods,
  });

  return {experimentalInputs, templateFlowchart};
}

type MethodPlan = ProcedureRunPlanNode['procedureRunPlanConditions'][number]['methodParametersPlans'][number];

/**
 * Extracts parameter values from a method plan, matching them to the template method's parameter order.
 * Similar to getValuesForMethod but for plan stored values instead of PMEI planned values.
 *
 * Plan values are stored as JSONScalar with different shapes:
 * - Data parameters: { value: TypedValue }
 * - Material inputs: { material: TypedValue, amount: TypedValue }
 * - Equipment: { equipment: TypedValue }
 */
function getValuesFromMethodPlan(
  templateMethod: ExperimentMethod,
  methodPlan: MethodPlan | undefined
): ExperimentalInputTypedValuesByComponentName {
  if (methodPlan == null) {
    return {
      parameters: templateMethod.dataParameterSettings.map(() => []),
      materialInputs: templateMethod.materialInputSettings.map(() => []),
      equipment: templateMethod.equipmentSettings.map(() => []),
    };
  }

  const dataParamValuesById = new Map<string, JSONScalar>(
    methodPlan.dataParameterValues.map((dpv) => [dpv.parameterId, dpv.value])
  );
  const materialInputValuesById = new Map<string, JSONScalar>(
    methodPlan.materialInputValues.map((miv) => [miv.materialInputId, miv.value])
  );
  const equipmentValuesById = new Map<string, JSONScalar>(
    methodPlan.equipmentValues.map((ev) => [ev.equipmentId, ev.value])
  );

  return {
    // Data parameters: stored as { value: TypedValue }
    parameters: templateMethod.dataParameterSettings.map((setting) => {
      const stored = dataParamValuesById.get(setting.version.id) as Record<string, unknown> | undefined;
      const value = stored?.value as TypedValue | undefined;
      return value != null ? [value] : [];
    }),

    // Material inputs: stored as { value: { material: TypedValue, amount: TypedValue } }
    // The REST API requires a 'value' wrapper around the content
    materialInputs: templateMethod.materialInputSettings.map((setting) => {
      const stored = materialInputValuesById.get(setting.version.id) as Record<string, unknown> | undefined;
      const innerValue = stored?.value as Record<string, unknown> | undefined;
      const material = (innerValue?.material as TypedValue | undefined) ?? null;
      const amount = (innerValue?.amount as TypedValue | undefined) ?? null;
      if (material == null && amount == null) {
        return [];
      }
      return [material, amount] as Array<TypedValue>;
    }),

    // Equipment: stored as { value: { equipment: TypedValue } }
    // The REST API requires a 'value' wrapper around the content
    equipment: templateMethod.equipmentSettings.map((setting) => {
      const stored = equipmentValuesById.get(setting.version.id) as Record<string, unknown> | undefined;
      const innerValue = stored?.value as Record<string, unknown> | undefined;
      const value = innerValue?.equipment as TypedValue | undefined;
      return value != null ? [value] : [];
    }),
  };
}

/**
 * Gets confirmation methods for a method's parameters based on the plan-level mapping.
 */
function getConfirmationMethodsForMethod(
  methodNode: ExperimentMethod,
  confirmationMethodsByParamVersionId: Map<string, ProcedureParameterConfirmationMethod>
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  return {
    parameters: methodNode.dataParameterSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    materialInputs: methodNode.materialInputSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
    equipment: methodNode.equipmentSettings.map(
      (setting) =>
        confirmationMethodsByParamVersionId.get(setting.version.id) ?? setting.version.confirmationMethod
    ),
  };
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_PLAN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunPlanGraphQL on ProcedureRunPlanNode {
    id
    description
    dataEntryMode
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        id
        ...ExperimentalInputsFlowchartGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    procedureFlowchartConfigVersionProxy {
      ...ExperimentalInputsProcedureFlowchartVersionProxyGraphQL
      ...InaccessibleInfoGraphQL
    }
    procedureRunPlanConditions {
      id
      name
      description
      position
      methodParametersPlans {
        templateMethodVersion {
          id
        }
        dataParameterValues {
          parameterId
          value
        }
        materialInputValues {
          materialInputId
          value
        }
        equipmentValues {
          equipmentId
          value
        }
      }
    }
    confirmationMethodByParamVersionId {
      paramVersionId
      confirmationMethod
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT}
`;
